import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Input } from 'Components';
import classNames from 'classnames';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import useAudienceTenantId from 'LearningManager/hooks/useAudienceTenantId';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';

const Property = observer(() => {
	const { assignUsersStore } = useStores();
	const { properties, status } = assignUsersStore;
	const { tenantId, isTenantResolved } = useAudienceTenantId(
		useAssignmentTenantId()
	);

	const {
		draft: {
			customSetup: { selectedProperties }
		},
		toggleAssignUsersSelection,
		setAssignUsersSelection
	} = useStepper();

	const [search, setSearch] = useState('');

	useEffect(() => {
		if (!isTenantResolved) return;
		assignUsersStore.fetchProperties(tenantId);
	}, [isTenantResolved, tenantId]);

	const filtered = search.trim()
		? properties.filter((p) =>
				p.name.toLowerCase().includes(search.toLowerCase())
		  )
		: properties;

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const numId = Number(id);
		const property = properties.find((p) => p.id === numId);
		if (!property) return;
		if (checked !== selectedProperties.some((p) => p.id === numId)) {
			toggleAssignUsersSelection('selectedProperties', {
				id: property.id,
				name: property.name,
				label: property.label
			});
		}
	};

	const handleSelectAll = () => {
		if (selectedProperties.length === filtered.length) {
			setAssignUsersSelection('selectedProperties', []);
		} else {
			setAssignUsersSelection(
				'selectedProperties',
				filtered.map((p) => ({
					id: p.id,
					name: p.name,
					label: p.label
				}))
			);
		}
	};

	const isAllSelected =
		filtered.length > 0 &&
		filtered.every((p) =>
			selectedProperties.some((sp) => sp.id === p.id)
		);
	const isLoading = status.fetchProperties === Status.Pending;

	return (
		<div id="lms-ca-assign-users-property">
			<div
				className={classNames(styles.filters, styles['filters--flex_end'])}
			>
				<Input.Search
					id="lms-ca-au-property-search"
					className={styles.search}
					placeholder="Search"
					suffix={<Icon type="search" role="img" />}
					// Keeps a suffix rendered, so the input isn't remounted mid-typing.
					allowClear
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>
			<div className={styles.header}>
				<Heading titleClassName={styles.title} title="Select properties" astrix={true} />
				{filtered.length > 1 && (
					<div
						id="lms-ca-au-property-select-all"
						className={styles.selectAllBtn}
						onClick={handleSelectAll}
					>
						{isAllSelected ? 'Deselect all' : 'Select all'}
					</div>
				)}
			</div>
			<div id="lms-ca-au-property-list" className={styles.container}>
				{isLoading && <div className={styles.loading}><Spin /></div>}
				{filtered.map((property) => {
					const isChecked = selectedProperties.some(
						(p) => p.id === property.id
					);
					return (
						<CheckBoxCard
							key={property.id}
							id={property.id}
							htmlId={`lms-ca-au-property-${property.id}`}
							icon={
								<Icon
									type={
										isChecked ? 'propOptFilled' : 'propOptIcon'
									}
								/>
							}
							title={property.name}
							desc={property.label}
							isChecked={isChecked}
							onChange={handleCheckboxChange}
						/>
					);
				})}
			</div>
		</div>
	);
});

export default Property;
